import React from 'react'
import { Button, type ButtonProps } from '@progress/kendo-react-buttons'
import { cn } from '@utils/mergeClassNames'
import KMK_LOADING from '@public/assets/common/kmk_loading.gif'
import { DotsLoader } from './DotsLoader'

type ButtonLoaderSize = 'xsmall' | 'small' | 'medium' | 'large'

const BUTTON_LOADER_SIZE: Record<ButtonLoaderSize, number> = {
  xsmall: 16,
  small: 24,
  medium: 32,
  large: 40,
}

export interface BaseButtonProps extends ButtonProps {
  loading?: boolean
  loadingText?: React.ReactNode
  preserveWidthOnLoading?: boolean
  loader?: {
    loaderClassName?: string
    loaderSize?: ButtonLoaderSize
    // 'ocg' shows the KMK validating animation used for OCG validation;
    // ordinary actions show the two-dot loader.
    variant?: 'dots' | 'ocg'
  }
}

const BaseButton = ({
  children,
  className,
  disabled = false,
  loading = false,
  loadingText,
  preserveWidthOnLoading = false,
  loader: {
    loaderClassName = '',
    loaderSize = 'small',
    variant: loaderVariant = 'dots',
  } = {},
  size = 'small',
  rounded = 'full',
  fillMode = 'solid',
  themeColor = 'dark',
  type = 'button',
  ...buttonProps
}: BaseButtonProps) => (
  <Button
    {...buttonProps}
    type={type}
    size={size}
    rounded={rounded}
    fillMode={fillMode}
    themeColor={themeColor}
    disabled={disabled || loading}
    aria-busy={loading}
    className={cn(
      'relative inline-flex min-h-10 cursor-pointer items-center justify-center gap-2 px-5 py-2',
      'text-sm font-normal leading-none shadow-none outline-none',
      'transition-colors duration-200',
      'disabled:cursor-not-allowed',
      className
    )}
  >
    {/* The loader sits before the label with a 6px gap (MIRA-4295 design).
        Kendo wraps children in its own text span, so this wrapper owns the
        gap. preserveWidthOnLoading is kept for callers but no longer
        overlays the loader on the label. */}
    <span className="inline-flex items-center justify-center gap-1.5">
      {loading && (
        <span
          className={cn(
            'inline-flex shrink-0 items-center justify-center leading-none',
            loaderClassName
          )}
        >
          {loaderVariant === 'ocg' ? (
            <img
              alt=""
              src={KMK_LOADING}
              width={BUTTON_LOADER_SIZE[loaderSize]}
              height={BUTTON_LOADER_SIZE[loaderSize]}
              className="block shrink-0 object-contain"
            />
          ) : (
            <DotsLoader />
          )}
        </span>
      )}
      <span className="inline-flex items-center justify-center">
        {loading && loadingText && !preserveWidthOnLoading
          ? loadingText
          : children}
      </span>
    </span>
  </Button>
)

export default BaseButton
